import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Box, Typography, Grid, Alert
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import { ActionButtons } from './shared/ActionButtons';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/apiHelpers';

function ManufacturerGotoPersons({ open, onClose, manufacturer, onContactChange }) {
  const [user, setUser] = useState(null);
  const [contactPersons, setContactPersons] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({ full_name: '', email: '' });
  const [newPerson, setNewPerson] = useState({ full_name: '', email: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const canEdit = user && ['admin', 'account_onboarding'].includes(user.role);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const response = await apiGet('/api/verify-token');
        setUser(response.data.user);
      } catch (error) {
        console.warn('Token verification failed:', error);
      }
    };
    checkUser();
  }, []);

  useEffect(() => {
    if (open && manufacturer) {
      loadContactPersons();
    }
  }, [open, manufacturer]);

  const loadContactPersons = async () => {
    if (!manufacturer?.manufacturer_id) return;
    
    setLoading(true);
    try {
      const response = await apiGet(`/api/manufacturers/${manufacturer.manufacturer_id}/goto-persons`);
      setContactPersons(response.data || []);
      setError('');
    } catch (error) {
      console.error('Load points of contact error:', error);
      setError('Failed to load points of contact');
      setContactPersons([]);
    } finally {
      setLoading(false);
    }
  };

  const addContactPerson = async (e) => {
    e.preventDefault();
    
    // Production-ready validation
    const trimmedName = newPerson.full_name.trim();
    const trimmedEmail = newPerson.email.trim().toLowerCase();
    
    if (!trimmedName || trimmedName.length < 2) {
      setError('Full name must be at least 2 characters');
      return;
    }
    if (trimmedName.length > 100) {
      setError('Full name must be less than 100 characters');
      return;
    }
    if (!trimmedEmail) {
      setError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Please enter a valid email address');
      return;
    }
    if (trimmedEmail.length > 254) {
      setError('Email must be less than 254 characters');
      return;
    }
    
    setSaving(true);
    try {
      await apiPost(`/api/manufacturers/${manufacturer.manufacturer_id}/goto-persons`, {
        full_name: trimmedName,
        email: trimmedEmail
      });
      setNewPerson({ full_name: '', email: '' });
      setShowAddForm(false);
      setError('');
      loadContactPersons();
      // Immediately refresh counts in parent
      if (onContactChange) {
        onContactChange();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to add point of contact';
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (person) => {
    setEditingId(person.id);
    setEditValues({ full_name: person.full_name, email: person.email });
  };

  const saveEdit = async () => {
    // Production-ready validation
    const trimmedName = editValues.full_name.trim();
    const trimmedEmail = editValues.email.trim().toLowerCase();
    
    if (!trimmedName || trimmedName.length < 2) {
      setError('Full name must be at least 2 characters');
      return;
    }
    if (trimmedName.length > 100) {
      setError('Full name must be less than 100 characters');
      return;
    }
    if (!trimmedEmail) {
      setError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Please enter a valid email address');
      return;
    }
    if (trimmedEmail.length > 254) {
      setError('Email must be less than 254 characters');
      return;
    }
    
    setSaving(true);
    try {
      await apiPut(`/api/manufacturers/${manufacturer.manufacturer_id}/goto-persons/${editingId}`, {
        full_name: trimmedName,
        email: trimmedEmail
      });
      setEditingId(null);
      setError('');
      loadContactPersons();
      // Immediately refresh counts in parent
      if (onContactChange) {
        onContactChange();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to update point of contact';
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValues({ full_name: '', email: '' });
  };

  const deletePerson = async (personId) => {
    if (window.confirm('Are you sure you want to delete this point of contact?')) {
      try {
        await apiDelete(`/api/manufacturers/${manufacturer.manufacturer_id}/goto-persons/${personId}`);
        setError('');
        loadContactPersons();
        // Immediately refresh counts in parent
        if (onContactChange) {
          onContactChange();
        }
      } catch (error) {
        setError('Failed to delete point of contact');
      }
    }
  };

  const handleClose = () => {
    if (saving) return; // Prevent closing during save operations
    
    setEditingId(null);
    setShowAddForm(false);
    setNewPerson({ full_name: '', email: '' });
    setError('');
    setLoading(false);
    setSaving(false);
    onClose(true); // Always refresh counts on close
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Points of Contact - {manufacturer?.manufacturer_name}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1">Current Points of Contact</Typography>
          {canEdit && (
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setShowAddForm(true)}
              size="small"
            >
              Add Person
            </Button>
          )}
        </Box>

        {showAddForm && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Add New Point of Contact</Typography>
            <form onSubmit={addContactPerson}>
              <Grid container spacing={2}>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={newPerson.full_name}
                    onChange={(e) => setNewPerson({...newPerson, full_name: e.target.value.slice(0, 100)})}
                    required
                    size="small"
                    disabled={saving}
                    slotProps={{
                      htmlInput: { maxLength: 100 }
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={newPerson.email}
                    onChange={(e) => setNewPerson({...newPerson, email: e.target.value.slice(0, 254)})}
                    required
                    size="small"
                    disabled={saving}
                    slotProps={{
                      htmlInput: { maxLength: 254 }
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button 
                      type="submit" 
                      variant="contained" 
                      size="small"
                      disabled={saving}
                    >
                      {saving ? 'Adding...' : 'Add'}
                    </Button>
                    <Button 
                      onClick={() => setShowAddForm(false)} 
                      size="small"
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
        )}

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Full Name</TableCell>
                <TableCell>Email</TableCell>
                {canEdit && <TableCell width="120">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {contactPersons.map((person) => (
                <TableRow key={person.id}>
                  <TableCell>
                    {editingId === person.id ? (
                      <TextField
                        size="small"
                        value={editValues.full_name}
                        onChange={(e) => setEditValues({...editValues, full_name: e.target.value})}
                        fullWidth
                      />
                    ) : (
                      person.full_name
                    )}
                  </TableCell>
                  <TableCell>
                    {editingId === person.id ? (
                      <TextField
                        size="small"
                        type="email"
                        value={editValues.email}
                        onChange={(e) => setEditValues({...editValues, email: e.target.value})}
                        fullWidth
                      />
                    ) : (
                      person.email
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell>
                      <ActionButtons
                        isEditing={editingId === person.id}
                        onEdit={() => startEdit(person)}
                        onSave={saveEdit}
                        onCancel={cancelEdit}
                        onDelete={() => deletePerson(person.id)}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {contactPersons.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canEdit ? 3 : 2} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No points of contact added yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ManufacturerGotoPersons;